import { ChangeEvent, useEffect, useMemo, useRef, useState } from "react";
import { useQueryClient } from "react-query";
import { useTranslation } from "react-i18next";

import euroOfficeService from "api/euroOfficeService";
import officeService from "api/officeService";
import Button from "components/Button";
import ConfirmModal from "components/ConfirmModal";
import { IDropdownObject } from "components/Dropdown/typings";
import DropdownMui from "components/DropdownMui";
import HollowButton from "components/HollowButton";
import Input from "components/Input";
import Loading from "components/Loading";
import { DocumentEditorKind } from "dto/EuroOffice/IEuroOffice";
import { IError } from "dto/IError";
import { IOffice } from "dto/Office/IOffice";
import { getErrorMessage } from "utils/getErrorMessage";
import { ENotificationType, notification } from "utils/notification";

import styles from "./styles.module.scss";
import { FC, IOfficeForm, OfficeFormErrors } from "./typings";

// Dane urzędu (Ustawienia → Urząd): nazwa, dane adresowe, NIP i REGON (kolumny Workspaces.Company_*) oraz herb
// gminy/miasta. Z tych danych korzystają powiadomienia e-mail Biura Interesanta (herb zamiast logo Noty, nazwa i adres
// w stopce) i CRU (NIP, REGON jednostki). Zmienia administrator albo właściciel firmy; pozostali widzą dane do odczytu.

const EMBLEM_TYPES = ["image/png", "image/jpeg"];
const EMBLEM_MAX_BYTES = 500 * 1024;
const ZIP_PATTERN = /^\d{2}-\d{3}$/;
const NIP_WEIGHTS = [6, 5, 7, 2, 3, 4, 5, 6, 7];

const digits = (value: string): string => value.replace(/\D/g, "");

const isValidNip = (value: string): boolean => {
  const nip = digits(value);
  if (nip.length !== 10) return false;
  const sum = NIP_WEIGHTS.reduce((acc, weight, i) => acc + weight * Number(nip[i]), 0);
  const control = sum % 11;
  return control !== 10 && control === Number(nip[9]);
};

const isValidRegon = (value: string): boolean => {
  const regon = digits(value);
  return regon.length === 9 || regon.length === 14;
};

const toForm = (office?: IOffice | null): IOfficeForm => ({
  name: office?.Name ?? "",
  nip: office?.Nip ?? "",
  regon: office?.Regon ?? "",
  address: office?.Address ?? "",
  zipCode: office?.ZipCode ?? "",
  city: office?.City ?? "",
  phone: office?.Phone ?? "",
  email: office?.Email ?? "",
  documentEditor: office?.DocumentEditor ?? "word",
});

const OfficeTab: FC = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { data, isLoading, error } = officeService.useGetOffice();
  const { mutate: save, isLoading: isSaving } = officeService.useSaveOffice();
  const { mutate: upload, isLoading: isUploading } = officeService.useUploadEmblem();
  const { mutate: remove, isLoading: isRemoving } = officeService.useRemoveEmblem();

  const office = data?.data ?? null;
  const canEdit = office?.CanEdit ?? false;

  // Edytor dokumentów urzędu — „Euro-Office" ma sens tylko, gdy BE ma skonfigurowany Document Server.
  const { data: euroOfficeStatus } = euroOfficeService.useGetStatus();
  const editorOptions = useMemo<IDropdownObject[]>(
    () => [
      { value: "word", name: t("office.editor.word") },
      { value: "euro-office", name: t("office.editor.euroOffice") },
    ],
    [t],
  );

  const [form, setForm] = useState<IOfficeForm>(() => toForm(office));
  const [confirmRemove, setConfirmRemove] = useState(false);
  const fileInput = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setForm(toForm(office));
  }, [office]);

  // Herb do podglądu — pobierany jako Blob (trasa wymaga tokenu), więc <img> dostaje adres obiektu w pamięci.
  const { data: emblemData } = officeService.useGetEmblem(office?.EmblemVersion, !!office?.HasEmblem);
  const emblemUrl = useMemo(
    () => (office?.HasEmblem && emblemData?.data ? URL.createObjectURL(emblemData.data) : null),
    [office?.HasEmblem, emblemData],
  );

  useEffect(() => {
    return () => {
      if (emblemUrl) URL.revokeObjectURL(emblemUrl);
    };
  }, [emblemUrl]);

  const errors = useMemo<OfficeFormErrors>(() => {
    const result: OfficeFormErrors = {};
    if (!form.name.trim()) result.name = t("office.validation.name");
    if (form.nip.trim() && !isValidNip(form.nip)) result.nip = t("office.validation.nip");
    if (form.regon.trim() && !isValidRegon(form.regon)) result.regon = t("office.validation.regon");
    if (form.zipCode.trim() && !ZIP_PATTERN.test(form.zipCode.trim())) result.zipCode = t("office.validation.zipCode");
    return result;
  }, [form, t]);

  const isDirty = useMemo(() => {
    const saved = toForm(office);
    return (Object.keys(saved) as (keyof IOfficeForm)[]).some((key) => saved[key] !== form[key]);
  }, [office, form]);

  const canSave = canEdit && isDirty && Object.keys(errors).length === 0;

  const notify = (type: ENotificationType, text: string) =>
    notification({ type, title: t("office.title"), text });

  const notifyError = (e: unknown) =>
    notify(
      ENotificationType.ERROR,
      getErrorMessage(e as IError) || (e instanceof Error ? e.message : "") || t("errors.somethingWentWrongShort"),
    );

  const applyOffice = (saved: IOffice) => {
    queryClient.setQueryData(officeService.queryKeys.getOffice, { data: saved });
    // nazwa urzędu i herb są podpowiadane w ustawieniach Biura Interesanta
    queryClient.invalidateQueries("ebiOfficeService.getSettings");
    queryClient.invalidateQueries("ebiOfficeService.getMailPreview");
  };

  const handleSave = () =>
    save(
      {
        Name: form.name.trim(),
        Nip: form.nip.trim() || null,
        Regon: form.regon.trim() || null,
        Address: form.address.trim() || null,
        ZipCode: form.zipCode.trim() || null,
        City: form.city.trim() || null,
        Phone: form.phone.trim() || null,
        Email: form.email.trim() || null,
        DocumentEditor: form.documentEditor,
      },
      {
        onSuccess: ({ data: saved }) => {
          applyOffice(saved);
          notify(ENotificationType.SUCCESS, t("office.saved"));
        },
        onError: notifyError,
      },
    );

  const handleChooseFile = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;

    if (!EMBLEM_TYPES.includes(file.type)) {
      notify(ENotificationType.ERROR, t("office.emblem.invalidType"));
      return;
    }

    if (file.size > EMBLEM_MAX_BYTES) {
      notify(ENotificationType.ERROR, t("office.emblem.tooLarge"));
      return;
    }

    upload(file, {
      onSuccess: ({ data: saved }) => {
        applyOffice(saved);
        queryClient.invalidateQueries(officeService.queryKeys.getEmblem);
        notify(ENotificationType.SUCCESS, t("office.emblem.uploaded"));
      },
      onError: notifyError,
    });
  };

  const handleRemove = () => {
    setConfirmRemove(false);
    remove(undefined, {
      onSuccess: ({ data: saved }) => {
        applyOffice(saved);
        notify(ENotificationType.SUCCESS, t("office.emblem.removed"));
      },
      onError: notifyError,
    });
  };

  const field = (key: keyof IOfficeForm, label: string) => (
    <div className={styles.field}>
      <Input
        value={form[key]}
        onChange={(value) => setForm({ ...form, [key]: value })}
        label={label}
        disabled={!canEdit}
      />
      {errors[key] && <div className={styles.error}>{errors[key]}</div>}
    </div>
  );

  if (isLoading) {
    return (
      <div className={styles.loading}>
        <Loading />
      </div>
    );
  }

  if (!office) {
    return (
      <div className={styles.wrapper}>
        <div className={styles.error}>{getErrorMessage(error) || t("errors.somethingWentWrongShort")}</div>
      </div>
    );
  }

  return (
    <div className={styles.wrapper}>
      <div className={styles.section}>
        <div className={styles.sectionTitle}>{t("office.title")}</div>
        <div className={styles.hint}>{t("office.description")}</div>
        {!canEdit && <div className={styles.hint}>{t("office.readOnly")}</div>}
      </div>

      <div className={styles.section}>
        {field("name", t("office.name"))}
        <div className={styles.row}>
          {field("nip", t("office.nip"))}
          {field("regon", t("office.regon"))}
        </div>
        {field("address", t("office.address"))}
        <div className={styles.row}>
          {field("zipCode", t("office.zipCode"))}
          {field("city", t("office.city"))}
        </div>
        <div className={styles.row}>
          {field("phone", t("office.phone"))}
          {field("email", t("office.email"))}
        </div>
      </div>

      <div className={styles.section}>
        <div className={styles.sectionTitle}>{t("office.editor.title")}</div>
        <div className={styles.hint}>{t("office.editor.description")}</div>
        {euroOfficeStatus && !euroOfficeStatus.Enabled && (
          <div className={styles.hint}>{t("office.editor.unavailable")}</div>
        )}
        <div className={styles.field}>
          <DropdownMui
            list={editorOptions}
            defaultValue={form.documentEditor}
            onChange={(value?: IDropdownObject) =>
              setForm({ ...form, documentEditor: (value?.value as DocumentEditorKind) ?? "word" })
            }
            disabled={!canEdit}
            labelPosition="none"
            variant="filled"
          />
        </div>
      </div>

      {canEdit && (
        <div className={styles.actions}>
          <Button text={t("common.save")} onClick={handleSave} disabled={!canSave || isSaving} />
        </div>
      )}

      <div className={styles.section}>
        <div className={styles.sectionTitle}>{t("office.emblem.title")}</div>
        <div className={styles.hint}>{t("office.emblem.description")}</div>

        <div className={styles.emblemBox}>
          {office.HasEmblem && emblemUrl ? (
            <img className={styles.emblem} src={emblemUrl} alt={t("office.emblem.alt")} />
          ) : (
            <div className={styles.hint}>{office.HasEmblem ? "" : t("office.emblem.none")}</div>
          )}
        </div>

        {canEdit && (
          <div className={styles.actions}>
            {office.HasEmblem && (
              <HollowButton
                text={t("office.emblem.remove")}
                onClick={() => setConfirmRemove(true)}
                disabled={isRemoving || isUploading}
              />
            )}
            <Button
              text={office.HasEmblem ? t("office.emblem.replace") : t("office.emblem.upload")}
              onClick={() => fileInput.current?.click()}
              disabled={isUploading || isRemoving}
            />
            <input
              type="file"
              accept={EMBLEM_TYPES.join(",")}
              ref={fileInput}
              className={styles.fileInput}
              onChange={handleChooseFile}
            />
          </div>
        )}
      </div>

      {confirmRemove && (
        <ConfirmModal
          title={t("office.emblem.removeTitle")}
          text={t("office.emblem.removeText")}
          acceptLabel={t("office.emblem.remove")}
          cancelLabel={t("common.cancel")}
          onAccept={handleRemove}
          onCancel={() => setConfirmRemove(false)}
          showDontShowNextTime={false}
        />
      )}
    </div>
  );
};

export default OfficeTab;
